import type { MutableRefObject } from 'react'
import type { AIOperation, AIResponse, ApiResponse, MindMapNode } from '@bandos/shared'
import { AIService } from '../../../services/ai/aiService'
import { aiServiceConfig } from '@bandos/shared'
import type { AISuggestion, SourceNodeInfo, AIStateSetters, AIRefs } from '../types/aiTypes'
import { calculateBatchPositions, type PositionCalculationDeps } from './aiPositioning'
import { getExistingSuggestionLabels } from './aiSuggestions'
import { executeAIOperation, type ExecuteAIOperationResult } from './aiOperationExecutor'

// ============================================================================
// GENERATION METHOD FACTORY
// ============================================================================

interface GenerationConfig {
  operation: AIOperation
  batchCount: number
  serviceCall: (
    projectId: string,
    nodeId: string,
    positions: { x: number; y: number }[],
    existingSuggestions?: string[],
    accumulatedFeedback?: string
  ) => Promise<ApiResponse<AIResponse>>
}

interface GenerationDeps {
  mindmapId: string | undefined
  getPositionDeps: () => PositionCalculationDeps
  suggestions: AISuggestion[]
  currentOperation: AIOperation | null
  sourceNodes: Map<string, SourceNodeInfo>
  lastOperationRef: MutableRefObject<string>
  setters: AIStateSetters
  refs: AIRefs
  waitForPendingSaves: () => Promise<void>
  callbacks: {
    onSuccess?: (operation: AIOperation, result: AIResponse) => void
    onError?: (operation: AIOperation, error: Error | string) => void
    onReady?: (nodeId: string, count: number) => void
    onRegenerateFeedbackForChildren?: (sourceNodeId: string) => void
  }
}

/**
 * Creates a generation method (generateIdeas, generateNeeds, etc.).
 * All follow identical logic — only the operation type, batch count, and service call differ.
 */
export function createGenerationMethod(
  config: GenerationConfig,
  deps: GenerationDeps
): (
  nodeId: string,
  node?: MindMapNode,
  existingSuggestions?: string[],
  preserveSuggestions?: boolean,
  userFeedback?: string
) => Promise<ExecuteAIOperationResult> {
  return async (
    nodeId: string,
    node?: MindMapNode,
    existingSuggestions?: string[],
    preserveSuggestions: boolean = false,
    userFeedback?: string
  ): Promise<ExecuteAIOperationResult> => {
    if (!deps.mindmapId) {
      return { success: false, error: 'MindmapId not available' }
    }

    const projectId = deps.mindmapId
    const positions = calculateBatchPositions(
      nodeId,
      config.batchCount,
      undefined,
      deps.getPositionDeps()
    )

    const suggestionsToPass =
      existingSuggestions ??
      getExistingSuggestionLabels(
        deps.suggestions,
        nodeId,
        config.operation,
        deps.currentOperation,
        deps.lastOperationRef.current
      )

    // Build accumulated feedback: existing history + new feedback
    const existingFeedbackHistory = deps.sourceNodes.get(nodeId)?.feedbackHistory ?? []
    const allFeedbacks = userFeedback?.trim()
      ? [...existingFeedbackHistory, userFeedback.trim()]
      : existingFeedbackHistory
    const accumulatedFeedback = allFeedbacks.length > 0 ? allFeedbacks.join('\n\n') : undefined

    return executeAIOperation(
      config.operation,
      nodeId,
      () =>
        config.serviceCall(
          projectId,
          nodeId,
          positions,
          suggestionsToPass.length > 0 ? suggestionsToPass : undefined,
          accumulatedFeedback
        ),
      node,
      preserveSuggestions,
      userFeedback,
      { mindmapId: deps.mindmapId, waitForPendingSaves: deps.waitForPendingSaves },
      deps.setters,
      deps.refs,
      deps.sourceNodes,
      deps.callbacks
    )
  }
}

// ============================================================================
// GENERATION CONFIGS
// ============================================================================

export const IDEA_GENERATION_CONFIG: GenerationConfig = {
  operation: 'generate_idea',
  batchCount: aiServiceConfig.IDEAS_TO_GENERATE * 2,
  serviceCall: AIService.generateIdeas,
}

export const NEEDS_GENERATION_CONFIG: GenerationConfig = {
  operation: 'generate_needs_from_venture',
  batchCount: 8,
  serviceCall: AIService.generateNeedsFromVenture,
}

export const SYNTHESIZE_NEEDS_FROM_RESEARCH_CONFIG: GenerationConfig = {
  operation: 'synthesize_needs_from_research',
  batchCount: 5,
  serviceCall: AIService.synthesizeNeedsFromResearch,
}
